/**
 * P4-R2C-PROD-FIX-001 (#796) -- may this client adopt a server-committed
 * draft version?
 *
 * WHY THIS IS A MODULE. The AI review approval now advances `draft_version`
 * server-side, so every caller must take the committed value or its next
 * versioned write reports a conflict the operator caused themselves. But
 * adopting blindly is the #794 defect: a version adopted without reconciling
 * the CONTENT that produced it lets a stale editor match the compare-and-swap
 * and overwrite another writer.
 *
 * The rule is therefore conditional, and a conditional rule inlined at a call
 * site can only be covered by source-shape assertions -- which R2C-003 proved
 * two shape-preserving mutants can defeat. Extracted here, the rule is
 * executable and a test can assert what it DOES.
 */

/** The committed version is ours to adopt only if we were the predecessor. */
export const ADOPT = "adopt";
/** Someone else also committed; surface divergence, change no version. */
export const CONFLICT = "conflict";
/** Nothing usable came back; leave all state alone. */
export const IGNORE = "ignore";

/**
 * Decide what to do with a `draft_version` a write reported as committed.
 *
 * `baseVersion` is the version the local draft was built on. `committed` is
 * what the server says it wrote.
 *
 * - equal to `baseVersion` -> IGNORE. Nothing was committed (a repeated
 *   approval writes nothing), so there is nothing to adopt and nothing has
 *   diverged.
 * - exactly `baseVersion + 1` -> ADOPT. Our write was the single intervening
 *   change, so the version and our content still correspond.
 * - any other integer -> CONFLICT. Another writer also committed and their
 *   content is not in our draft, so adopting would rebase unreconciled edits.
 * - not an integer -> IGNORE. Never guess a version.
 */
export function adoptionDecision(baseVersion, committed) {
  if (!Number.isInteger(committed)) {
    return { action: IGNORE, version: null };
  }
  if (!Number.isInteger(baseVersion)) {
    //: no local base to compare against -- the server value is all we have,
    //: and there is no local content it could invalidate.
    return { action: ADOPT, version: committed };
  }
  if (committed === baseVersion) {
    //: NOTHING WAS COMMITTED. A repeated approval marks no new node, so the
    //: server writes nothing and reports the version we already hold. There
    //: is nothing to adopt and nothing has diverged, so reporting a conflict
    //: here would render a false "v8 -> v8" banner -- and would overwrite a
    //: GENUINE conflict object with a meaningless one. Do nothing.
    return { action: IGNORE, version: committed };
  }
  if (committed === baseVersion + 1) {
    return { action: ADOPT, version: committed };
  }
  //: Any other value means another writer also committed. Their content is
  //: not in our draft, so adopting would rebase unreconciled edits.
  return { action: CONFLICT, version: committed };
}

/** The operator-facing text for a divergence found this way. */
export function divergenceMessage(baseVersion, committed) {
  return `Another session has modified this funnel (v${baseVersion} → v${committed}). `
    + `Your changes are still here; refresh to get the latest version before saving.`;
}
